import { parseTranscript } from './parseTranscript.js';
import type {
  ConversationEventType,
  TranscriptDocument,
  TranscriptTurn,
} from './parseTranscript.js';

export interface ConversationSession {
  id: string;
  label: string;
  startTimestamp: string | null;
  endTimestamp: string | null;
  transcript: TranscriptDocument;
}

export interface ConversationDocument {
  id: string | null;
  title: string | null;
  sessions: ConversationSession[];
}

const INVALID_JSON_MESSAGE = 'The selected file is not valid JSON.';
const INVALID_CONVERSATION_MESSAGE =
  'The selected file does not contain a supported conversation.';
const DEFAULT_SESSION_ID = 'session-1';
const DEFAULT_SESSION_LABEL = 'Session 1';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

function optionalTimestamp(value: unknown): string | null {
  const timestamp = optionalString(value);
  return timestamp !== null && !Number.isNaN(Date.parse(timestamp))
    ? timestamp
    : null;
}

function requiredString(value: unknown): string {
  const result = optionalString(value);
  if (result === null) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  return result;
}

function requiredTimestamp(value: unknown): string {
  const result = optionalTimestamp(value);
  if (result === null) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  return result;
}

function asArray(value: unknown): unknown[] {
  if (value === undefined || value === null) {
    return [];
  }

  if (!Array.isArray(value)) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  return value;
}

function parseSessionEvent(
  value: unknown,
  eventType: ConversationEventType,
  sessionId: string,
  index: number,
): TranscriptTurn {
  if (!isRecord(value)) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  const timestamp = requiredTimestamp(value.ts);
  const speaker = requiredString(
    eventType === 'transcript' ? value.speaker : value.sender,
  );

  return {
    id: `${sessionId}-${eventType}-${index}`,
    eventType,
    speaker,
    text: requiredString(value.text),
    timestamp,
    startSeconds: null,
    endSeconds: null,
    dialogueAct: null,
    labels: [],
  };
}

function parseUnifiedEvent(
  value: unknown,
  meetingId: string,
  index: number,
): TranscriptTurn {
  if (!isRecord(value)) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  const rawType = requiredString(value.type);
  const eventType: ConversationEventType =
    rawType === 'transcript'
      ? 'transcript'
      : rawType === 'chat'
        ? 'chat'
        : rawType === 'user_query'
          ? 'userQuery'
          : (() => {
              throw new Error(INVALID_CONVERSATION_MESSAGE);
            })();

  return {
    id: `${meetingId}-${eventType}-${index}`,
    eventType,
    speaker: requiredString(value.source),
    text: requiredString(value.text),
    timestamp: requiredTimestamp(value.timestamp),
    startSeconds: null,
    endSeconds: null,
    dialogueAct: null,
    labels: [],
  };
}

function sortByTimestamp(turns: TranscriptTurn[]): TranscriptTurn[] {
  return turns
    .map((turn, index) => ({ turn, index }))
    .sort((left, right) => {
      const timestampDifference =
        Date.parse(left.turn.timestamp ?? '') -
        Date.parse(right.turn.timestamp ?? '');
      return timestampDifference === 0
        ? left.index - right.index
        : timestampDifference;
    })
    .map(({ turn }) => turn);
}

function getSessionLabel(
  value: Record<string, unknown>,
  sessionId: string,
): string {
  return (
    optionalString(value.source_meeting_id) ??
    (sessionId.startsWith('session-') ? sessionId : `Session ${sessionId}`)
  );
}

function parseSession(value: unknown, index: number): ConversationSession {
  if (!isRecord(value)) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  const rawSessionId = optionalString(value.session_id) ?? String(index + 1);
  const sessionId = `session-${rawSessionId}`;
  const transcriptTurns = asArray(value.transcript).map((turn, turnIndex) =>
    parseSessionEvent(turn, 'transcript', sessionId, turnIndex),
  );
  const chatTurns = asArray(value.chat_messages).map((turn, turnIndex) =>
    parseSessionEvent(turn, 'chat', sessionId, turnIndex),
  );
  const userQueryTurns = asArray(value.user_queries).map((turn, turnIndex) =>
    parseSessionEvent(turn, 'userQuery', sessionId, turnIndex),
  );
  const turns = sortByTimestamp([
    ...transcriptTurns,
    ...chatTurns,
    ...userQueryTurns,
  ]);

  if (turns.length === 0) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  return {
    id: sessionId,
    label: getSessionLabel(value, rawSessionId),
    startTimestamp: optionalTimestamp(value.start_ts),
    endTimestamp: optionalTimestamp(value.end_ts),
    transcript: {
      meetingId: optionalString(value.source_meeting_id),
      turns,
    },
  };
}

function getMeetingTitle(value: Record<string, unknown>): string | null {
  if (!isRecord(value.meeting_info)) {
    return null;
  }

  return optionalString(value.meeting_info.subject);
}

function parseUnifiedMeeting(
  value: unknown,
  index: number,
): ConversationSession {
  if (!isRecord(value)) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  const meetingId =
    optionalString(value.meeting_id) ?? `meeting-${String(index + 1)}`;
  const turns = sortByTimestamp(
    asArray(value.events).map((event, eventIndex) =>
      parseUnifiedEvent(event, meetingId, eventIndex),
    ),
  );
  if (turns.length === 0) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  const meetingInfo = isRecord(value.meeting_info)
    ? value.meeting_info
    : undefined;
  const schedule =
    meetingInfo !== undefined && isRecord(meetingInfo.schedule)
      ? meetingInfo.schedule
      : undefined;

  return {
    id: meetingId,
    label:
      getMeetingTitle(value) ??
      optionalString(value.source_meeting_id) ??
      meetingId,
    startTimestamp: optionalTimestamp(schedule?.start_ts),
    endTimestamp: optionalTimestamp(schedule?.end_ts),
    transcript: {
      meetingId,
      turns,
    },
  };
}

function wrapTranscript(transcript: TranscriptDocument): ConversationDocument {
  return {
    id: transcript.meetingId,
    title: null,
    sessions: [
      {
        id: DEFAULT_SESSION_ID,
        label: DEFAULT_SESSION_LABEL,
        startTimestamp: null,
        endTimestamp: null,
        transcript,
      },
    ],
  };
}

function parseJsonConversation(source: string): ConversationDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch {
    throw new Error(INVALID_JSON_MESSAGE);
  }

  if (!isRecord(parsed)) {
    throw new Error(INVALID_CONVERSATION_MESSAGE);
  }

  if (Array.isArray(parsed.turns)) {
    return wrapTranscript(parseTranscript(source, 'transcript.json'));
  }

  if (Array.isArray(parsed.sessions)) {
    const sessions = parsed.sessions.map(parseSession);
    if (sessions.length === 0) {
      throw new Error(INVALID_CONVERSATION_MESSAGE);
    }

    return {
      id: optionalString(parsed.id),
      title: getMeetingTitle(parsed),
      sessions,
    };
  }

  if (Array.isArray(parsed.meetings)) {
    const sessions = parsed.meetings.map(parseUnifiedMeeting);
    if (sessions.length === 0) {
      throw new Error(INVALID_CONVERSATION_MESSAGE);
    }

    return {
      id: null,
      title: null,
      sessions,
    };
  }

  if (
    Array.isArray(parsed.transcript) ||
    Array.isArray(parsed.chat_messages) ||
    Array.isArray(parsed.user_queries)
  ) {
    return {
      id: optionalString(parsed.id),
      title: getMeetingTitle(parsed),
      sessions: [parseSession(parsed, 0)],
    };
  }

  throw new Error(INVALID_CONVERSATION_MESSAGE);
}

export function canParseConversation(source: string, fileName = ''): boolean {
  const trimmedSource = source.trim();
  const normalizedFileName = fileName.trim().toLowerCase();
  const likelyTextTranscript =
    /(?:^|[-_.\s])(transcript|chat|conversation)(?:[-_.\s]|$)/.test(
      normalizedFileName,
    );
  const isJson =
    normalizedFileName.endsWith('.json') ||
    (!normalizedFileName.endsWith('.txt') && trimmedSource.startsWith('{'));

  if (!isJson && !likelyTextTranscript) {
    return false;
  }

  try {
    parseConversation(source, fileName);
    return true;
  } catch {
    return false;
  }
}

export function parseConversation(
  source: string,
  fileName = '',
): ConversationDocument {
  const trimmedSource = source.trim();
  const normalizedFileName = fileName.trim().toLowerCase();
  const isJson =
    normalizedFileName.endsWith('.json') ||
    (!normalizedFileName.endsWith('.txt') && trimmedSource.startsWith('{'));

  return isJson
    ? parseJsonConversation(trimmedSource)
    : wrapTranscript(parseTranscript(trimmedSource, fileName));
}
